import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Session timeout: 24 hours in milliseconds
  const SESSION_TIMEOUT = 24 * 60 * 60 * 1000;

  const isSessionValid = () => {
    const loginTime = localStorage.getItem('loginTime');
    const sessionId = sessionStorage.getItem('sessionId');

    if (!loginTime || !sessionId) {
      return false;
    }

    const currentTime = new Date().getTime();
    const timeDifference = currentTime - parseInt(loginTime);

    // Check if session has expired (24 hours)
    return timeDifference < SESSION_TIMEOUT;
  };

  const clearSession = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');
    sessionStorage.removeItem('sessionId');
    setUser(null);
  };

  useEffect(() => {
    // Check if user is already logged in and session is valid
    const token = localStorage.getItem('authToken');
    if (token && isSessionValid()) {
      const user = JSON.parse(localStorage.getItem('user'));
      setUser(user);
    } else {
      // Clear invalid session
      clearSession();
    }
    setLoading(false);
  }, []);

  // Set up activity tracking and session timeout
  useEffect(() => {
    if (!user) return;

    let timeoutId;

    const resetTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set timeout for 24 hours of inactivity
      timeoutId = setTimeout(() => {
        alert('Your session has expired due to inactivity. Please log in again.');
        clearSession();
      }, SESSION_TIMEOUT);
    };

    const handleActivity = () => {
      // Update last activity time
      localStorage.setItem('loginTime', new Date().getTime().toString());
      resetTimeout();
    };

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initial timeout setup
    resetTimeout();

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user]);

  // Check session validity on page focus (when user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (user && !isSessionValid()) {
        alert('Your session has expired. Please log in again.');
        clearSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  const login = async (email, password) => {
    try {
      console.log('AuthContext: Starting login...');
      const response = await authService.login(email, password);
      console.log('AuthContext: Got response:', response);
      
      // Check if response has error status code
      if (response.statusCode && response.statusCode >= 400) {
        const errorBody = JSON.parse(response.body);
        throw new Error(errorBody.error || 'Login failed');
      }
      
      const respBody = JSON.parse(response.body);
      console.log('AuthContext: Parsed body:', respBody);
      
      if (respBody.error) {
        throw new Error(respBody.error);
      }

      // Set session data
      const currentTime = new Date().getTime().toString();
      const sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

      setUser(respBody.user);
      localStorage.setItem('authToken', respBody.user.token);
      localStorage.setItem('user', JSON.stringify(respBody.user));
      localStorage.setItem('loginTime', currentTime);
      sessionStorage.setItem('sessionId', sessionId);

      console.log('AuthContext: Login completed successfully');
      return response;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);

      // Check if response has error status code
      if (response.statusCode && response.statusCode >= 400) {
        const errorBody = JSON.parse(response.body);
        throw new Error(errorBody.error || 'Registration failed');
      }

      const respBody = JSON.parse(response.body);
      if (respBody.error) {
        throw new Error(respBody.error);
      }

      // Don't set user session for registration - they need to activate first
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    clearSession();
  };

  const updateUser = async (updateData) => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }

      const response = await authService.updateUser(user.id, updateData);
      const respBody = JSON.parse(response.body);
      
      if (respBody.error) {
        throw new Error(respBody.error);
      }

      // Update local user state
      const updatedUser = respBody.user;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      return response;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}